export enum RideStatus {
  PENDING = 'PENDING',
  SEARCHING_DRIVER = 'SEARCHING_DRIVER',
  DRIVER_ASSIGNED = 'DRIVER_ASSIGNED',
  DRIVER_ARRIVED = 'DRIVER_ARRIVED',
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NO_DRIVERS_AVAILABLE = 'NO_DRIVERS_AVAILABLE',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  DRIVER_TIMEOUT = 'DRIVER_TIMEOUT',
}

export const RIDE_STATUS_ID_MAP: Record<number, RideStatus> = {
  1: RideStatus.PENDING,
  2: RideStatus.SEARCHING_DRIVER,
  3: RideStatus.DRIVER_ASSIGNED,
  4: RideStatus.DRIVER_ARRIVED,
  5: RideStatus.STARTED,
  6: RideStatus.COMPLETED,
  7: RideStatus.CANCELLED,
  8: RideStatus.NO_DRIVERS_AVAILABLE,
  9: RideStatus.PAYMENT_FAILED,
  10: RideStatus.DRIVER_TIMEOUT,
};

export const RIDE_STATUS_ID_REVERSE_MAP: Record<RideStatus, number> = {
  [RideStatus.PENDING]: 1,
  [RideStatus.SEARCHING_DRIVER]: 2,
  [RideStatus.DRIVER_ASSIGNED]: 3,
  [RideStatus.DRIVER_ARRIVED]: 4,
  [RideStatus.STARTED]: 5,
  [RideStatus.COMPLETED]: 6,
  [RideStatus.CANCELLED]: 7,
  [RideStatus.NO_DRIVERS_AVAILABLE]: 8,
  [RideStatus.PAYMENT_FAILED]: 9,
  [RideStatus.DRIVER_TIMEOUT]: 10,
};

export const ACTIVE_RIDE_STATUSES = [
  RideStatus.DRIVER_ARRIVED,
  RideStatus.STARTED,
] as const;

export const PENDING_RIDE_STATUSES = [
  RideStatus.PENDING,
  RideStatus.SEARCHING_DRIVER,
] as const;

export const ASSIGNED_RIDE_STATUSES = [
  RideStatus.DRIVER_ASSIGNED,
] as const;

export const COMPLETED_RIDE_STATUSES = [
  RideStatus.COMPLETED,
  RideStatus.CANCELLED,
] as const;

export const normalizeRideStatus = (status: string | number | null | undefined): RideStatus => {
  if (!status) return RideStatus.PENDING;

  if (typeof status === 'number') {
    return RIDE_STATUS_ID_MAP[status] || RideStatus.PENDING;
  }

  const upperStatus = status.toUpperCase();
  const statusKey = Object.keys(RideStatus).find(
    key => RideStatus[key as keyof typeof RideStatus] === upperStatus
  );

  if (statusKey) {
    return RideStatus[statusKey as keyof typeof RideStatus];
  }

  return RideStatus.PENDING;
};

export const isActiveRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return ACTIVE_RIDE_STATUSES.includes(normalizedStatus as any);
};

export const isPendingRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return PENDING_RIDE_STATUSES.includes(normalizedStatus as any);
};

export const isAssignedRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return ASSIGNED_RIDE_STATUSES.includes(normalizedStatus as any);
};

export const isCompletedRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return COMPLETED_RIDE_STATUSES.includes(normalizedStatus as any);
};

export const canAcceptRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return normalizedStatus === RideStatus.PENDING || normalizedStatus === RideStatus.SEARCHING_DRIVER;
};

export const canArriveRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return normalizedStatus === RideStatus.DRIVER_ASSIGNED;
};

export const canStartRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return normalizedStatus === RideStatus.DRIVER_ARRIVED;
};

export const canCompleteRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return normalizedStatus === RideStatus.STARTED;
};

export const canCancelRide = (status: string | number | null | undefined): boolean => {
  const normalizedStatus = normalizeRideStatus(status);
  return !isCompletedRide(status) && normalizedStatus !== RideStatus.STARTED;
};

